import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { API_ENDPOINTS } from '@core/constants/api-endpoints';
import { Api } from '@core/service/api/api';
import { Session } from '@core/service/session/session';
import { DependentData } from '@features/pages/collection/interface/collection-iterface';
import { EventbusService } from '@shared/services/event-bus/eventbus-service';
import { SocketService } from '@shared/services/socket/socket-service';
import { Toast } from '@shared/services/toast/toast';
import { Observable, tap } from 'rxjs';
import { ChangePasswordPayload, ChangePasswordResponse, DropdownsResponse, ForgotPasswordPayload, ForgotPasswordResponse, GoogleSSOPayload, HeaderDataResponse, LoginPayload, LoginResponse, ResetPasswordPayload, ResetPasswordResponse, SignUpPayload, SignUpResponse, UserSession, VerifyOtpPayload, VerifyOtpResponse } from '../../interface/auth-interface';


@Injectable({
  providedIn: 'root'
})
export class Auth {

  private api = inject(Api);
  private session = inject(Session);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private socketService = inject(SocketService);
  private toast = inject(Toast);


  // Store dependent data temporarily before authentication
  private pendingDependentData: DependentData | null = null;
  private eventBus = inject(EventbusService);

  login(payload: LoginPayload): Observable<LoginResponse> {
    // const payload: LoginPayload = { email, password };

    console.log("login", API_ENDPOINTS.AUTH.LOGIN);
    return this.api.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, payload).pipe(
      tap((response) => {
        // this.session.setSession({
        //   token: response.token,
        //   user: response.user
        // });
      })
    );
  }

  setData(response: LoginResponse) {
    this.toast.success(response.message || 'Signed in successfully via Google!');
    this.session.setSession(response);

    // Check if there's pending dependent data
    const pendingData = this.getPendingData();

    setTimeout(() => {
      let data: DependentData | null = null;
      if (pendingData) {
        // If there's dependent data, use it (e.g., emit to EventBus or use for navigation)
        data = pendingData;
        console.log('Pending dependent data after login:', data);
        // TODO: Use the dependent data as needed
        // For now, still navigate to dashboard
        this.clearPendingData();
      }
      const redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');

      const targetUrl = redirectTo || '/layout/dashboard?tab=1';

      this.router.navigateByUrl(targetUrl);

      setTimeout(() => {
        if (data) this.eventBus.emitTyped('NUDGING_DATA', data);
      }, 300)
    }, 300)
    this.socketService.init();
  }

  logout() {
    this.socketService.clear();
    this.session.clearSession();
    this.router.navigateByUrl("/layout/home");

    // this.
  }

  isLoggedIn() {
    return this.session.isLoggedIn();
  }

  /** Fetch signup dropdowns in a single API call */
  getDropdowns(): Observable<DropdownsResponse> {
    return this.api.post<DropdownsResponse>(API_ENDPOINTS.AUTH.DROPDOWNS, {}).pipe(
      tap({
        error: (err) => {
          // this.toaster.show(err.message || 'Failed to load dropdowns', 'error');
          console.log('err', err)
        }
      })
    );
  }

  /** Signup method */
  signup(payload: SignUpPayload): Observable<SignUpResponse> {
    return this.api.post<SignUpResponse>(API_ENDPOINTS.AUTH.SIGNUP, payload).pipe(
      tap(
        {
          next: (response) => {
            console.log("signup", response);

          },
          error: (err) => {

            console.log(err);
          }
        })
    );
  }


  /** Forgot Password method */
  forgotPassword(payload: ForgotPasswordPayload): Observable<ForgotPasswordResponse> {
    return this.api
      .post<ForgotPasswordResponse>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, payload)
      .pipe(
        tap((response) => {
          console.log('Forgot password response:', response);
        })
      );
  }

  /** Verify OTP method */
  verifyOtp(payload: VerifyOtpPayload): Observable<VerifyOtpResponse> {
    return this.api
      .post<VerifyOtpResponse>(API_ENDPOINTS.AUTH.VERIFY_OTP, payload)
      .pipe(
        tap((response) => {
          console.log('OTP verification response:', response);
        })
      );
  }

  /** Refresh Access Token */
  refreshToken(refresh_token: string): Observable<UserSession> {
    return this.api
      .post<LoginResponse>(API_ENDPOINTS.AUTH.REFRESH_TOKEN, { refresh_token })
      .pipe(
        tap((response) => {
          if (!response.access_token || !response.refresh_token) {
            throw new Error('Invalid refresh token response');
          }

          const sessionData: UserSession = {
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            user: response.user,
            message: response.message
          };

          this.session.setSession(sessionData);
          console.log('Token refreshed successfully', sessionData);
        }),
        // Map the LoginResponse to UserSession for easier typing
        // This allows the interceptor to directly use the returned session
        // Alternatively, you can return response as is and let the interceptor handle mapping
        // Here we return the session itself
        tap(() => {
          console.log("getSessionSignal()", this.session.session())
        })
      );
  }

  /**
 * Change password API call
 * @param payload - old and new passwords
 * @returns Observable of ChangePasswordResponse
 */
  changePassword(payload: ChangePasswordPayload): Observable<ChangePasswordResponse> {
    return this.api.post<ChangePasswordResponse>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      payload
    ).pipe(
      tap({
        next: (response) => {
          console.log('Password changed successfully', response);
        },
        error: (err) => {
          console.error('Failed to change password', err);
        }
      })
    );
  }

  /**
  * Google SSO auto signup/login
  * @param payload - Google auth code
  */

  googleSSO(payload: GoogleSSOPayload): Observable<LoginResponse> {
    return this.api.post<LoginResponse>(
      API_ENDPOINTS.AUTH.GOOGLE_CALLBACK,  // e.g., '/auth/sso/google/callback'
      payload
    ).pipe(
      tap({
        next: (response) => {
          console.log('Google SSO successful', response);
        },
        error: (err) => {
          console.error('Google SSO failed', err);
        }
      })
    )
  }

  /** Reset Password method */
  resetPassword(payload: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.api
      .post<ResetPasswordResponse>(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload)
      .pipe(
        tap({
          next: (response) => {
            console.log('Reset password successful:', response);
          },
          error: (err) => {
            console.error('Reset password failed:', err);
          }
        })
      );
  }

  getHeaderData() {
    return this.api.post<HeaderDataResponse>(API_ENDPOINTS.AUTH.GET_HEADER_DATA, {}).pipe(
      tap({
        next: (response) => {
          console.log('Credits data:', response);
        },
        error: (err) => {
          console.error('Failed to get credits data', err);
        }
      })
    );
  }

  /** Store dependent data before redirecting to sign-in */
  setPendingData(data: DependentData | null): void {
    this.pendingDependentData = data;
    console.log('Stored pending dependent data:', data);
  }

  /** Retrieve stored dependent data */
  getPendingData(): DependentData | null {
    return this.pendingDependentData;
  }

  /** Clear stored dependent data after use */
  private clearPendingData(): void {
    this.pendingDependentData = null;
  }



}
